import { CButton, CCol, CFormGroup, CInput, CRow } from "@coreui/react";
import React, { useCallback, useEffect, useState } from "react";
import { FieldErrors } from "../form/FieldErrors";
import Errors, { errorsAreSame, getFieldErrors } from "../../models/errors";
import { StoreProductPricing } from "../../models/product";
import { getItem } from "../../api/generics";
import Store from "../../models/store";
import { SUCCESS } from "../../utils/constants/tags";
import SingleStoreSelect from "../store/SingleStoreSelect";

interface ProductCustomPricingFormItemProps {
  value: StoreProductPricing;
  disabled?: boolean;
  onDelete: (_: StoreProductPricing) => void;
  onChange: (_: StoreProductPricing) => void;
  errors: Errors;
}

const ProductCustomPricingFormItem: React.FC<
  ProductCustomPricingFormItemProps
> = ({ value, disabled, onDelete, onChange, errors }) => {
  const [editingItem, setEditingItem] = useState<StoreProductPricing>(value);
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const onDeleteClick = useCallback(() => {
    onDelete(editingItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  const onWholesaleThresholdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseInt(e.target.value))) {
        amount = parseInt(e.target.value);
      }
      const newEditingItem: StoreProductPricing = {
        ...editingItem,
        wholesaleThreshold: amount,
      };
      onChange(newEditingItem);
    },
    [editingItem, onChange]
  );

  const onSuperWholesaleThresholdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseInt(e.target.value))) {
        amount = parseInt(e.target.value);
      }
      const newEditingItem: StoreProductPricing = {
        ...editingItem,
        superWholesaleThreshold: amount,
      };
      onChange(newEditingItem);
    },
    [editingItem, onChange]
  );

  const onPriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseFloat(e.target.value))) {
        amount = parseFloat(e.target.value);
      }
      const newEditingItem: StoreProductPricing = {
        ...editingItem,
        price: amount,
      };
      onChange(newEditingItem);
    },
    [editingItem, onChange]
  );

  const onWholesalePriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseFloat(e.target.value))) {
        amount = parseFloat(e.target.value);
      }
      const newEditingItem: StoreProductPricing = {
        ...editingItem,
        wholesalePrice: amount,
      };
      onChange(newEditingItem);
    },
    [editingItem, onChange]
  );

  const onSuperWholesalePriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseFloat(e.target.value))) {
        amount = parseFloat(e.target.value);
      }
      const newEditingItem: StoreProductPricing = {
        ...editingItem,
        superWholesalePrice: amount,
      };
      onChange(newEditingItem);
    },
    [editingItem, onChange]
  );

  const onStoreChange = useCallback(
    (newStore: Store | null) => {
      setCurrentStore(newStore);
      const newEditingItem: StoreProductPricing = {
        ...editingItem,
        storeId: newStore !== null ? newStore.id : undefined,
      };
      setEditingItem(newEditingItem);
      onChange(newEditingItem);
    },
    [editingItem, onChange]
  );

  const fetchStore = async (id: number) => {
    const storeStatus = await getItem<Store>(`/stores/${id}/`);
    if (storeStatus.status === SUCCESS) {
      if (storeStatus.data !== undefined) {
        setCurrentStore(storeStatus.data);
      }
    }
  };

  useEffect(() => {
    setEditingItem(value ? value : {});

    if (value?.storeId) {
      fetchStore(value.storeId);
    } else {
      setCurrentStore(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="section border rounded mb-2 p-2">
      <CFormGroup>
        <CRow>
          <CCol md={2} className={"mt-2"}>
            Sucursal:
          </CCol>
          <CCol>
            <SingleStoreSelect
              value={currentStore}
              onChange={onStoreChange}
              disabled={disabled}
            ></SingleStoreSelect>
            <FieldErrors
              errors={getFieldErrors("accountId", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
        <CRow className={"mt-2"}>
          <CCol md={2} className={"mt-2"}>
            Lim. Mayorista:
          </CCol>
          <CCol>
            <CInput
              type="number"
              placeholder="Cantidad Mayorista"
              value={editingItem.wholesaleThreshold}
              onChange={onWholesaleThresholdChange}
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("wholesaleThreshold", errors) as string[]}
            ></FieldErrors>
          </CCol>
          <CCol md={2} className={"mt-2"}>
            Lim. Super Mayorista:
          </CCol>
          <CCol>
            <CInput
              type="number"
              placeholder="Cantidad Supermayorista"
              value={editingItem.superWholesaleThreshold}
              onChange={onSuperWholesaleThresholdChange}
            ></CInput>
            <FieldErrors
              errors={
                getFieldErrors("superWholesaleThreshold", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
        </CRow>
        {/* Precios propios de la sucursal. Cada uno es opcional: si se deja
            vacio se usa el precio general del producto. */}
        <CRow className={"mt-2"}>
          <CCol md={2} className={"mt-2"}>
            Precio Unit.:
          </CCol>
          <CCol>
            <CInput
              type="number"
              placeholder="Usa el precio del producto"
              value={editingItem.price}
              onChange={onPriceChange}
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("price", errors) as string[]}
            ></FieldErrors>
          </CCol>
          <CCol md={2} className={"mt-2"}>
            Precio Mayorista:
          </CCol>
          <CCol>
            <CInput
              type="number"
              placeholder="Usa el precio del producto"
              value={editingItem.wholesalePrice}
              onChange={onWholesalePriceChange}
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("wholesalePrice", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
        <CRow className={"mt-2"}>
          <CCol md={2} className={"mt-2"}>
            Precio Super May.:
          </CCol>
          <CCol>
            <CInput
              type="number"
              placeholder="Usa el precio del producto"
              value={editingItem.superWholesalePrice}
              onChange={onSuperWholesalePriceChange}
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("superWholesalePrice", errors) as string[]}
            ></FieldErrors>
          </CCol>
          <CCol md={2}></CCol>
          <CCol></CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow>
          <CCol>
            <CButton
              className="btn btn-danger float-right"
              onClick={onDeleteClick}
              disabled={disabled}
            >
              <i className="fa fa-trash"></i>
            </CButton>
          </CCol>
        </CRow>
      </CFormGroup>
    </div>
  );
};

const propsAreEqual = (
  prevItemProps: ProductCustomPricingFormItemProps,
  nextItemProps: ProductCustomPricingFormItemProps
): boolean => {
  const areEqual =
    prevItemProps.value.id === nextItemProps.value.id &&
    prevItemProps.value.storeId === nextItemProps.value.storeId &&
    prevItemProps.value.superWholesaleThreshold ===
      nextItemProps.value.superWholesaleThreshold &&
    prevItemProps.value.wholesaleThreshold ===
      nextItemProps.value.wholesaleThreshold &&
    prevItemProps.value.price === nextItemProps.value.price &&
    prevItemProps.value.wholesalePrice ===
      nextItemProps.value.wholesalePrice &&
    prevItemProps.value.superWholesalePrice ===
      nextItemProps.value.superWholesalePrice &&
    errorsAreSame(prevItemProps.errors, nextItemProps.errors);

  return areEqual;
};

export default React.memo(ProductCustomPricingFormItem, propsAreEqual);
